/**
 * Detail collapse for a pair.
 */
import * as React from 'react';
import { HeartHandshake, Calendar, CalendarPlus } from 'lucide-react';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { usePairHistory } from '@/hooks/usePairs';
import { PAIR_DETAIL_TAB_CONFIGS, type PairDetailTab } from '@/components/pairs/pair-configs';
import type { PairDetailRecord } from '@/types/remote/pair.types';
import {
  PairDetailsTab,
  PairDissociateAction,
  PairHistoryTimeline,
} from './pair-detail-tabs/PairDetailsTab';
import {
  canAddOneOffSession,
  deriveDisplay,
  formatDate,
  getAddOneOffSessionTooltip,
  getPairSwapLabel,
} from './pair-table-utils';

/** Props for {@link PairDetails}. */
interface PairDetailsProps {
  readonly pair: PairDetailRecord;
  readonly onEdit?: (pairId: string) => void;
  readonly onDissociate?: (pairId: string) => void;
  readonly onViewSessions?: () => void;
  readonly onAddOneOffSession?: () => void;
}

/** Details vs history tab inside the expanded pair row. */
type ContentTab = PairDetailTab;

/** Expanded-row collapse: details tab + lazy-loaded history timeline. */
export function PairDetails({
  pair,
  onEdit,
  onDissociate,
  onViewSessions,
  onAddOneOffSession,
}: PairDetailsProps) {
  const [activeTab, setActiveTab] = React.useState<ContentTab>('details');

  const { data: history, isLoading: historyLoading } = usePairHistory(pair.id, {
    enabled: activeTab === 'history',
  });

  const kpis = pair.kpis;
  const { isArchived, reportsWritten, reportsTotal, sessionsPlanned, statusCfg, progCfg } =
    deriveDisplay(pair);
  const canEdit = !!onEdit && !isArchived;

  const tabs: Array<TabItem<ContentTab>> = PAIR_DETAIL_TAB_CONFIGS.map(
    ({ id, label, icon: Icon }) => ({
      id,
      label,
      icon: <Icon className="w-3 h-3" />,
    }),
  );

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <HeartHandshake className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{getPairSwapLabel(pair)}</h3>
              <LabelBadge
                label={statusCfg.label}
                icon={statusCfg.icon}
                variant={statusCfg.badgeVariant}
                color={statusCfg.badgeColor}
                size="sm"
              />
              {progCfg && (
                <LabelBadge
                  label={progCfg.label}
                  variant="outlined"
                  color={progCfg.color}
                  size="sm"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">
              Binôme créé le {formatDate(pair.createdAt)}
            </p>
          </div>
        </div>

        <div className="details-view-header-right">
          {onViewSessions && (
            <Button
              variant="outline"
              size="sm"
              icon={Calendar}
              onClick={onViewSessions}
              tooltipText="Voir les séances"
            >
              Séances
            </Button>
          )}
          {onAddOneOffSession && (
            <Button
              variant="outline"
              size="sm"
              icon={CalendarPlus}
              onClick={onAddOneOffSession}
              disabled={!canAddOneOffSession(pair)}
              tooltipText={getAddOneOffSessionTooltip(pair)}
            >
              Séance ponctuelle
            </Button>
          )}
          <PairDissociateAction
            isArchived={isArchived}
            onDissociate={onDissociate ? () => onDissociate(pair.id) : undefined}
          />
        </div>
      </div>

      <Tabs items={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id)} />

      {activeTab === 'details' && (
        <PairDetailsTab
          pair={pair}
          kpis={kpis}
          sessionsPlanned={sessionsPlanned}
          reportsWritten={reportsWritten}
          reportsTotal={reportsTotal}
          isArchived={isArchived}
          canEdit={canEdit}
          onEdit={onEdit}
        />
      )}

      {activeTab === 'history' && (
        <div className="mt-6">
          <PairHistoryTimeline loading={historyLoading} events={history} />
        </div>
      )}
    </>
  );
}

/** {@link DataTable} expanded-row adapter around {@link PairDetails}. */
export const renderPairDetails = (
  pair: PairDetailRecord,
  onEdit?: (pairId: string) => void,
  onDissociate?: (pairId: string) => void,
  onViewSessions?: () => void,
  onAddOneOffSession?: () => void,
): React.ReactNode => (
  <PairDetails
    pair={pair}
    onEdit={onEdit}
    onDissociate={onDissociate}
    onViewSessions={onViewSessions}
    onAddOneOffSession={onAddOneOffSession}
  />
);
